/**
 * Compliance → SDF Obligations (DPDPA §10) - Phase 8 batch G item 4.
 *
 * Singleton per tenant. Captures Significant Data Fiduciary designation +
 * §17(2) sub-obligations (DPO appointed, annual DPIA, annual data audit,
 * other prescribed measures).
 */
import { useCallback, useEffect, useState } from 'react';
import {
  RefreshCw, Loader2, AlertTriangle, CheckCircle2, Save, ShieldAlert, Award, Download,
} from 'lucide-react';
import { GlassCard, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { PageContainer, PageHeader } from '@/components/viz/PageHeader';
import { getSdf, upsertSdf, downloadSdfDocument, type SdfObligation } from '@/lib/api';

function toDateInput(iso: string | null | undefined): string {
  if (!iso) return '';
  return new Date(iso).toISOString().slice(0, 10);
}

export function ComplianceSdf(): JSX.Element {
  const [sdf, setSdf] = useState<SdfObligation | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Form state
  const [designated, setDesignated] = useState(false);
  const [designationBasis, setDesignationBasis] = useState('');
  const [designatedAt, setDesignatedAt] = useState('');
  const [dpoAppointed, setDpoAppointed] = useState(false);
  const [dpoName, setDpoName] = useState('');
  const [dpoEmail, setDpoEmail] = useState('');
  const [dpoAppointedAt, setDpoAppointedAt] = useState('');
  const [annualDpia, setAnnualDpia] = useState('');
  const [annualAudit, setAnnualAudit] = useState('');
  const [reviewerNote, setReviewerNote] = useState('');

  const refresh = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const { sdf: s } = await getSdf();
      setSdf(s);
      if (s) {
        setDesignated(s.isDesignatedSdf);
        setDesignationBasis(s.designationBasis ?? '');
        setDesignatedAt(toDateInput(s.designatedAt));
        setDpoAppointed(s.dpoAppointed);
        setDpoName(s.dpoName ?? '');
        setDpoEmail(s.dpoEmail ?? '');
        setDpoAppointedAt(toDateInput(s.dpoAppointedAt));
        setAnnualDpia(toDateInput(s.annualDpiaCompletedAt));
        setAnnualAudit(toDateInput(s.annualDataAuditCompletedAt));
        setReviewerNote(s.lastReviewerNote ?? '');
      }
    } catch (e) { setError(e instanceof Error ? e.message : 'failed to load SDF'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { void refresh(); }, [refresh]);

  async function handleSave() {
    setError(null);
    if (designated) {
      if (!designationBasis.trim() || !designatedAt) {
        setError('Designation requires basis + designated-at date');
        return;
      }
    }
    if (dpoAppointed) {
      if (!dpoName.trim() || !dpoEmail.trim() || !dpoAppointedAt) {
        setError('DPO appointment requires name + email + appointed-at date');
        return;
      }
    }
    setSaving(true);
    try {
      const input: Parameters<typeof upsertSdf>[0] = {
        isDesignatedSdf: designated,
        dpoAppointed,
        ...(designated && designationBasis.trim() ? { designationBasis: designationBasis.trim() } : {}),
        ...(designated && designatedAt ? { designatedAt: new Date(designatedAt).toISOString() } : {}),
        ...(dpoAppointed && dpoName.trim() ? { dpoName: dpoName.trim() } : {}),
        ...(dpoAppointed && dpoEmail.trim() ? { dpoEmail: dpoEmail.trim() } : {}),
        ...(dpoAppointed && dpoAppointedAt ? { dpoAppointedAt: new Date(dpoAppointedAt).toISOString() } : {}),
        ...(annualDpia ? { annualDpiaCompletedAt: new Date(annualDpia).toISOString() } : {}),
        ...(annualAudit ? { annualDataAuditCompletedAt: new Date(annualAudit).toISOString() } : {}),
        ...(reviewerNote.trim() ? { lastReviewerNote: reviewerNote.trim() } : {}),
      };
      await upsertSdf(input);
      await refresh();
    } catch (e) { setError(e instanceof Error ? e.message : 'save failed'); }
    finally { setSaving(false); }
  }

  const dpiaOverdue = annualDpia
    ? Date.now() - new Date(annualDpia).getTime() > 365 * 86_400_000
    : true;
  const auditOverdue = annualAudit
    ? Date.now() - new Date(annualAudit).getTime() > 365 * 86_400_000
    : true;

  return (
    <PageContainer>
      <PageHeader
        title="Significant Data Fiduciary obligations (DPDPA §10)"
        subtitle="Designation, DPO appointment, annual DPIA, annual data audit. Singleton per tenant. Drives the §17 control on the Compliance Coverage rollup."
        badges={[
          { label: '§17(1) designation', variant: 'glass' },
          { label: '§17(2)(a) DPO', variant: 'glass' },
          { label: '§17(2)(b) annual DPIA + audit', variant: 'glass' },
          ...(loading ? [{ label: 'Loading', variant: 'glass' as const }]
            : error ? [{ label: 'Load error', variant: 'warning' as const }]
            : [{ label: 'Live', variant: 'success' as const }]),
        ]}
        actions={
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="glass"
              onClick={() => downloadSdfDocument()}
              title="Export DPDPA Section 10 + 17 attestation as HTML for board sign-off"
            >
              <Download className="h-3.5 w-3.5 mr-1" /> Board report
            </Button>
            <Button size="sm" variant="glass" onClick={() => void refresh()} disabled={loading}>
              <RefreshCw className={`h-3.5 w-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
          </div>
        }
      />
      {error && (
        <div className="glass-lite border border-warning/40 rounded-md p-3 mb-3 text-xs flex items-start gap-2">
          <AlertTriangle className="h-4 w-4 text-warning shrink-0 mt-0.5" />
          <div className="flex-1">{error}</div>
          <Button size="sm" variant="glass" onClick={() => setError(null)}>Dismiss</Button>
        </div>
      )}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <GlassCard className="p-4">
          <div className="flex items-center gap-2 mb-2"><Award className="h-4 w-4 text-primary" /><strong className="text-sm">§17(1) Designation</strong></div>
          <Badge variant={designated ? 'success' : 'glass'} className="text-[10px]">{designated ? 'Designated SDF' : 'Not designated'}</Badge>
          {designated && designatedAt && <div className="text-[10px] text-muted-foreground mt-1">Since {new Date(designatedAt).toLocaleDateString()}</div>}
        </GlassCard>
        <GlassCard className="p-4">
          <div className="flex items-center gap-2 mb-2"><CheckCircle2 className="h-4 w-4 text-primary" /><strong className="text-sm">§17(2)(a) DPO</strong></div>
          <Badge variant={dpoAppointed ? 'success' : 'critical'} className="text-[10px]">{dpoAppointed ? 'Appointed' : 'Not appointed'}</Badge>
          {dpoAppointed && dpoName && <div className="text-[10px] text-muted-foreground mt-1">{dpoName} · {dpoEmail}</div>}
        </GlassCard>
        <GlassCard className="p-4">
          <div className="flex items-center gap-2 mb-2"><ShieldAlert className="h-4 w-4 text-primary" /><strong className="text-sm">§17(2)(b) annual</strong></div>
          <div className="flex gap-2">
            <Badge variant={annualDpia && !dpiaOverdue ? 'success' : 'critical'} className="text-[10px]">DPIA {annualDpia && !dpiaOverdue ? '✓' : 'overdue'}</Badge>
            <Badge variant={annualAudit && !auditOverdue ? 'success' : 'critical'} className="text-[10px]">Audit {annualAudit && !auditOverdue ? '✓' : 'overdue'}</Badge>
          </div>
        </GlassCard>
      </div>
      <GlassCard className="p-5">
        <CardHeader className="p-0 mb-3">
          <CardTitle>SDF declaration + obligation tracker</CardTitle>
          <CardDescription clamp>Update whenever the DPB notifies designation OR you complete an annual obligation.</CardDescription>
        </CardHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
          <div className="space-y-3">
            <div className="glass-lite rounded p-3 space-y-2">
              <label className="flex items-center gap-2 font-medium"><input type="checkbox" checked={designated} onChange={(e) => setDesignated(e.target.checked)} /> Designated as Significant Data Fiduciary under §17(1)</label>
              {designated && (
                <>
                  <div><label className="text-muted-foreground block mb-0.5">Designation basis</label>
                    <textarea value={designationBasis} onChange={(e) => setDesignationBasis(e.target.value)} rows={2} maxLength={2000}
                      placeholder="DPB Notification # / self-declared (cite reasoning)"
                      className="w-full glass-lite text-sm rounded px-2 py-1 bg-background/40 border border-border/40" /></div>
                  <div><label className="text-muted-foreground block mb-0.5">Designated at</label>
                    <input type="date" value={designatedAt} onChange={(e) => setDesignatedAt(e.target.value)}
                      className="w-full glass-lite text-sm rounded px-2 py-1 bg-background/40 border border-border/40" /></div>
                </>
              )}
            </div>
            <div className="glass-lite rounded p-3 space-y-2">
              <label className="flex items-center gap-2 font-medium"><input type="checkbox" checked={dpoAppointed} onChange={(e) => setDpoAppointed(e.target.checked)} /> §17(2)(a) DPO appointed</label>
              {dpoAppointed && (
                <>
                  <div><label className="text-muted-foreground block mb-0.5">DPO name</label>
                    <input type="text" value={dpoName} onChange={(e) => setDpoName(e.target.value)} maxLength={200}
                      className="w-full glass-lite text-sm rounded px-2 py-1 bg-background/40 border border-border/40" /></div>
                  <div><label className="text-muted-foreground block mb-0.5">DPO email</label>
                    <input type="email" value={dpoEmail} onChange={(e) => setDpoEmail(e.target.value)} maxLength={200}
                      className="w-full glass-lite text-sm rounded px-2 py-1 bg-background/40 border border-border/40" /></div>
                  <div><label className="text-muted-foreground block mb-0.5">DPO appointed at</label>
                    <input type="date" value={dpoAppointedAt} onChange={(e) => setDpoAppointedAt(e.target.value)}
                      className="w-full glass-lite text-sm rounded px-2 py-1 bg-background/40 border border-border/40" /></div>
                </>
              )}
            </div>
          </div>
          <div className="space-y-3">
            <div className="glass-lite rounded p-3 space-y-2">
              <div className="font-medium">§17(2)(b) annual obligations</div>
              <div><label className="text-muted-foreground block mb-0.5">Last annual DPIA completed</label>
                <input type="date" value={annualDpia} onChange={(e) => setAnnualDpia(e.target.value)}
                  className="w-full glass-lite text-sm rounded px-2 py-1 bg-background/40 border border-border/40" />
                <div className="text-[10px] text-muted-foreground mt-0.5">Triggers overdue past 365 days.</div></div>
              <div><label className="text-muted-foreground block mb-0.5">Last annual data audit completed</label>
                <input type="date" value={annualAudit} onChange={(e) => setAnnualAudit(e.target.value)}
                  className="w-full glass-lite text-sm rounded px-2 py-1 bg-background/40 border border-border/40" /></div>
            </div>
            <div className="glass-lite rounded p-3">
              <label className="text-muted-foreground block mb-0.5">Reviewer note</label>
              <textarea value={reviewerNote} onChange={(e) => setReviewerNote(e.target.value)} rows={3} maxLength={4000}
                placeholder="Notes from the most recent review (saved with the record)."
                className="w-full glass-lite text-sm rounded px-2 py-1 bg-background/40 border border-border/40" />
            </div>
          </div>
        </div>
        <div className="flex justify-end pt-3">
          <Button size="sm" data-action="disc.comp_sdf.a01" onClick={() => void handleSave()} disabled={saving}>
            {saving ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-1" />}
            Save SDF declaration
          </Button>
        </div>
        {sdf?.lastReviewedAt && (
          <div className="text-[10px] text-muted-foreground mt-2 text-right">
            Last reviewed {new Date(sdf.lastReviewedAt).toLocaleString()} by <code className="font-mono">{sdf.lastReviewedByActorId?.slice(0, 8) ?? '?'}…</code>
          </div>
        )}
      </GlassCard>
    </PageContainer>
  );
}
